import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  CASES_KEY,
  INCOMING_KEY,
  NOTICES_KEY,
  ProblemError,
  RECORD_KEY,
  refusalCode,
  withdrawHandover,
  type HandoverOut,
} from "@/api";

/** What a 409 on „Anfrage zurückziehen" means (contract §3.5: `reasons=[state]` for `not_pending`). */
const WITHDRAW_REFUSED: Record<string, (to: string) => string> = {
  declined: (to) => `${to} hat die Übergabe inzwischen abgelehnt.`,
  expired: () => "Die Anfrage ist inzwischen verfallen.",
  withdrawn: () => "Die Anfrage ist schon zurückgezogen.",
};

export interface Withdrawal {
  /** The sentence to announce — empty where the screen was left instead. */
  sentence: string;
  /** Whether the request is no longer open afterwards (withdrawn now, or answered meanwhile). */
  closed: boolean;
  /**
   * How the page shows the sentence (UX gate round 1, #3): withdrawn now is
   * `success`, answered meanwhile is news (`neutral`), not reached is a
   * `conflict` — the request is still out.
   */
  tone: "neutral" | "success" | "conflict";
}

/**
 * „Anfrage zurückziehen" — one per record (record fix wave), held by the
 * record screen and pressed from the head's waiting line, its one button
 * since the fact sheet shows the request without one (UX gate round 3, #8):
 * one request, one busy state, one sentence.
 *
 * Busy until the record has been read again: the open request vanishes from
 * the screen in the same moment the button stops saying „Wird
 * zurückgezogen …" — never a button that looks ready on a request that is
 * already gone.
 *
 * 409 `accepted` is the one that changes place (UX ruling 10, F-8): the
 * colleague said yes a moment ago, the Vorgang is theirs, and this record
 * would answer 404 on its next read — swallowing the one thing worth
 * saying. So the register opens instead and says it (`state.notice`,
 * `useRouteNotice`); the resolved sentence is then empty.
 */
export function useWithdrawHandover(caseId: string, caseTitle: string) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // A second button reaching this before the re-render has marked it busy.
  const running = useRef(false);

  async function refresh() {
    await Promise.all(
      [RECORD_KEY(caseId), CASES_KEY, INCOMING_KEY, NOTICES_KEY].map((queryKey) =>
        client.invalidateQueries({ queryKey }),
      ),
    );
  }

  async function settle(open: HandoverOut): Promise<Withdrawal> {
    try {
      await withdrawHandover(open.id);
      return { sentence: "Anfrage zurückgezogen.", closed: true, tone: "success" };
    } catch (cause) {
      const code = refusalCode(cause);
      if (code === "accepted") {
        navigate("/vorgaenge", {
          state: {
            notice: `${open.toDisplayName} hat „${caseTitle}“ übernommen — der Vorgang steht nicht mehr in deinem Register.`,
          },
        });
        return { sentence: "", closed: true, tone: "neutral" };
      }
      const sentence = code !== null ? WITHDRAW_REFUSED[code] : undefined;
      if (sentence !== undefined) return { sentence: sentence(open.toDisplayName), closed: true, tone: "neutral" };
      return {
        sentence: cause instanceof ProblemError ? cause.userMessage : "Nicht zurückgezogen — Server nicht erreichbar.",
        closed: false,
        tone: "conflict",
      };
    }
  }

  async function withdraw(open: HandoverOut): Promise<Withdrawal> {
    if (running.current) return { sentence: "", closed: false, tone: "neutral" };
    running.current = true;
    setBusy(true);
    try {
      const outcome = await settle(open);
      await refresh();
      return outcome;
    } finally {
      running.current = false;
      setBusy(false);
    }
  }

  return { busy, withdraw };
}
